import * as i0 from '@angular/core';
import { inject, Injectable, makeEnvironmentProviders, ENVIRONMENT_INITIALIZER } from '@angular/core';
import { createConfigTokens } from '@odx/angular/utils';
import { DOCUMENT } from '@angular/common';
import { WindowRef } from '@odx/angular';
import { BehaviorSubject, distinctUntilChanged, shareReplay } from 'rxjs';

/**
 * Determines whether the user's system preferences indicate a preference for dark themes.
 * This is achieved by querying the '(prefers-color-scheme: dark)' media feature, which reflects
 * the user's preference for a light or dark color scheme in their operating system's UI.
 *
 * @param {Window} view - The window object, typically `window`, from which to access the matchMedia method.
 * @returns {boolean} `true` if the user prefers a dark theme, otherwise `false`.
 *
 * @example
 * Example usage in a web application to toggle themes:
 * ```ts
 * if (prefersDarkTheme(window)) {
 *   document.body.classList.add('dark-theme');
 * } else {
 *   document.body.classList.remove('dark-theme');
 * }
 * ```
 */
function prefersDarkTheme(view) {
    return !!view.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
}

const Theme = {
    DARK: 'dark',
    LIGHT: 'light',
};
const availableThemes = Object.values(Theme);

/**
 * Resolves the specified theme by checking if it exists in the predefined list of available themes.
 * This function ensures that only valid themes are used in the application, providing a safeguard
 * against invalid theme names and facilitating the dynamic application of themes.
 *
 * @param {string | null} [theme] - The name of the theme to resolve. It's optional and can be `null`.
 * @returns {Theme | null} - Returns the resolved `Theme` object if the theme is found within the list of `availableThemes`;
 * otherwise, returns `null` to indicate the theme is not recognized or available.
 *
 * @example
 * ```ts
 * // Assuming 'dark' and 'light' are in the availableThemes array
 * console.log(resolveTheme('dark')); // Outputs the 'dark' Theme object
 * console.log(resolveTheme('unknown')); // Outputs null
 *
 * // Using resolveTheme to apply a user-selected theme
 * const userSelectedTheme = getUserSelectedTheme(); // This function gets the user's theme choice
 * const themeToApply = resolveTheme(userSelectedTheme);
 * if (themeToApply) {
 *   applyTheme(themeToApply); // This function applies the resolved theme to the application
 * } else {
 *   console.warn('Selected theme is not available.');
 * }
 * ```
 */
function resolveTheme(theme) {
    return availableThemes.find((t) => t === theme) ?? null;
}

/**
 * The key used to store the selected theme in `Storage`.
 * This constant ensures consistent access to the theme setting across the application.
 */
const THEME_STORAGE_NAME = '@odx/angular/theming:SelectedTheme';
/**
 * Saves the specified theme to storage. This function abstracts the storage interaction,
 * providing a fail-safe mechanism to avoid exceptions if the storage is not available.
 *
 * @param {Storage | null | undefined} storage - The storage mechanism to use, typically `localStorage` or `sessionStorage`.
 * @param {Theme} theme - The theme to save. Should be a value that is recognized by the application's theming system.
 * @throws Will silently catch and ignore any exceptions caused by storage limitations or permissions.
 *
 * @example
 * ```ts
 * // Example usage with localStorage
 * saveTheme(localStorage, 'dark');
 *
 * // Using sessionStorage instead
 * saveTheme(sessionStorage, 'light');
 * ```
 */
function saveTheme(storage, theme) {
    try {
        storage?.setItem(THEME_STORAGE_NAME, theme);
    }
    catch {
        //
    }
}
/**
 * Reads the theme from storage, attempting to resolve it to a valid application theme.
 * If the theme cannot be found or is invalid, `null` is returned. This function also provides
 * error handling for any storage access issues, ensuring the application can gracefully handle
 * scenarios where storage is not available or accessible.
 *
 * @param {Storage | null} [storage] - The storage mechanism to use, typically `localStorage` or `sessionStorage`.
 * @returns {Theme | null} - The resolved theme if found and valid; otherwise, `null`.
 * @throws Will silently catch and ignore any exceptions caused by storage access, returning `null`.
 *
 * @example
 * ```ts
 * // Reading theme from localStorage
 * const theme = readTheme(localStorage);
 * if (theme) {
 *   applyTheme(theme); // Assuming applyTheme is a function to apply the theme
 * } else {
 *   console.warn('No saved theme found or theme is invalid.');
 * }
 * ```
 */
function readTheme(storage) {
    try {
        return resolveTheme(storage?.getItem(THEME_STORAGE_NAME)) ?? null;
    }
    catch {
        return null;
    }
}

const THEME_ATTRIBUTE_NAME = 'odxTheme';
/**
 * Manages theming for the application. It allows for dynamic theme selection, automatically saves theme preferences,
 * and applies the user's preferred theme or the default theme based on configuration settings.
 *
 * The service leverages the BehaviorSubject pattern to reactively handle theme changes across the application,
 * ensuring that subscribers are notified whenever the theme changes. It also interacts with the DOM directly
 * to apply the current theme and with the provided storage mechanism to persist theme preferences.
 */
class ThemingService {
    constructor() {
        this.config = injectThemingConfig();
        this.document = inject(DOCUMENT);
        this.windowRef = inject(WindowRef);
        this.theme$$ = new BehaviorSubject(this.getInitialTheme());
        /**
         * An observable stream of the current theme, emitting values whenever the theme changes.
         * Subscribers to this observable can react to theme changes in real-time.
         *
         * @emits {Theme} The currently selected theme.
         */
        this.theme$ = this.theme$$.pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
        this.theme$.subscribe((theme) => {
            this.document.body.setAttribute(THEME_ATTRIBUTE_NAME, theme);
            saveTheme(this.config.storage, theme);
        });
    }
    /**
     * Selects and applies a given theme by updating the BehaviorSubject and persisting the preference.
     *
     * @param {Theme} theme - The theme to apply.
     */
    selectTheme(theme) {
        this.theme$$.next(theme);
    }
    /**
     * Retrieves the currently selected theme.
     *
     * @returns {Theme} The currently selected theme.
     */
    getSelectedTheme() {
        return this.theme$$.getValue();
    }
    /**
     * Toggles between dark and light themes, depending on the current theme.
     */
    toggleDarkMode() {
        const currentTheme = this.getSelectedTheme();
        if (currentTheme === Theme.DARK) {
            this.enableLightMode();
        }
        else {
            this.enableDarkMode();
        }
    }
    /**
     * Enables the dark mode theme.
     */
    enableDarkMode() {
        this.selectTheme(Theme.DARK);
    }
    /**
     * Enables the light mode theme.
     */
    enableLightMode() {
        this.selectTheme(Theme.LIGHT);
    }
    getInitialTheme() {
        let selectedTheme = readTheme(this.config.storage);
        if (!selectedTheme && this.config.autoDetect && prefersDarkTheme(this.windowRef.nativeWindow)) {
            selectedTheme = Theme.DARK;
        }
        return selectedTheme ?? this.config.defaultTheme;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ThemingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ThemingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ThemingService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

/**
 * Utility created by `createConfigTokens` for managing theming configuration tokens and functions.
 * Provides easy access to theming configuration, default values, and dependency injection utilities.
 */
const { ThemingConfig, injectThemingConfig, provideThemingConfig } = createConfigTokens('Theming', '@odx/angular/theme', {
    autoDetect: false,
    defaultTheme: Theme.LIGHT,
    storage: null,
});
/**
 * Provides the theming configuration to the application and initializes the `ThemingService`
 * based on the provided configuration. This function simplifies the setup of a dynamic theming
 * system, integrating with Angular's environment initializers to ensure proper initialization.
 *
 * @template D - The dependencies required by the theming configuration, if any.
 * @param {ConfigProvider<Partial<ThemingConfig>, D>} config - The theming configuration or a configuration factory provider.
 * @returns {EnvironmentProviders} An array of providers for setting up theming in the application.
 *
 * @example
 * ```ts
 * // Providing a simple theming configuration
 * provideTheming({
 *   autoDetect: true,
 *   defaultTheme: Theme.DARK,
 *   storage: localStorage,
 * });
 *
 * // Providing a theming configuration with dependencies
 * provideTheming({
 *   useFactory: (deps: Dependencies) => ({
 *     autoDetect: deps.setting,
 *     defaultTheme: Theme.DARK,
 *     storage: localStorage,
 *   }),
 *   deps: [SomeDependency],
 * });
 * ```
 */
function provideTheming(config) {
    return makeEnvironmentProviders([
        provideThemingConfig(config),
        {
            provide: ENVIRONMENT_INITIALIZER,
            useFactory: () => () => inject(ThemingService),
            deps: [ThemingConfig],
            multi: true,
        },
    ]);
}

/**
 * Generated bundle index. Do not edit.
 */

export { THEME_ATTRIBUTE_NAME, THEME_STORAGE_NAME, Theme, ThemingConfig, ThemingService, availableThemes, injectThemingConfig, prefersDarkTheme, provideTheming, provideThemingConfig, readTheme, resolveTheme, saveTheme };
